/**
 * The boundary between the app and pronunciation scoring. UI and game code depend only on these
 * types; implementations (ONNX Runtime Web in a Web Worker, a future native module, a mock) are
 * swappable.
 */

/** Sample rate of all audio passed to a scorer. */
export const SCORER_SAMPLE_RATE = 16000;

export interface ScoreRequest {
  /** One spoken word: mono PCM samples in [-1, 1] at `SCORER_SAMPLE_RATE`. */
  audio: Float32Array;
  /** Target pronunciation as IPA phonemes, e.g. ['ɹ', 'ɛ', 'd'] for "red". */
  targetPhonemes: readonly string[];
  /** A whole word (default) or a practice syllable such as "rah" (judged more leniently). */
  kind?: 'word' | 'syllable';
  /** A young voice (8 and under): judged with the threshold for young children. */
  young?: boolean;
}

export interface PhonemeScore {
  phoneme: string;
  /** Probability in [0, 1] that the phoneme was produced acceptably. */
  score: number;
  /** IPA of the sound most likely produced instead; null when it was left out. */
  heardAs?: string | null;
}

export interface ScoreResult {
  phonemes: PhonemeScore[];
  /** Index of the practised sound in `phonemes` (-1 when the word lacks it). */
  targetIndex: number;
  /** `phonemes[targetIndex].score`, or 0. */
  targetScore: number;
  /** Good enough to count: the practised sound scored at least the acceptance threshold. */
  accepted: boolean;
  /** Not accepted but close to the threshold. */
  nearMiss: boolean;
  /** The acceptance threshold the target sound was judged against (for criterion shaping). */
  threshold?: number;
  /** False when too little speech was heard to judge (silence, noise, a cough). */
  heard: boolean;
  /**
   * Sounds the recognizer heard in the attempt (greedy decoding), whatever was said: many more
   * than the word has means a different phrase ("droplets of water" for "rain").
   */
  heardSounds?: number;
}

export type ScorerLoadProgress = (loadedBytes: number, totalBytes: number) => void;

export interface SpeechScorer {
  /** Loads the model (cached after the first download); resolves when scoring is possible. */
  load(onProgress?: ScorerLoadProgress): Promise<void>;
  score(request: ScoreRequest): Promise<ScoreResult>;
  dispose(): void;
}
